import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionBQualityQuantity = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const surveyId = localStorage.getItem("survey_id") || "defaultSurvey";

  const [form, setForm] = useState({ b1: "", b2: "", b3: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
    const sectionB = surveyData[surveyId]?.sections?.b;
    if (sectionB) setForm(sectionB);
  }, [surveyId]);

  // Use stable internal values (keys), display translated labels
  const ratingOptions = [
    { value: "Good", label: t.global.rating_good },
    { value: "Satisfactory", label: t.global.rating_satisfactory },
    { value: "Poor", label: t.global.rating_poor },
  ];

  const yesNoOptions = [
    { value: "YES", label: t.global.yes },
    { value: "NO", label: t.global.no },
  ];

  const ratingValueMap = { Good: 1, Satisfactory: 2, Poor: 3 };
  const yesNoValueMap = { YES: 1, NO: 2 };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.b1.trim()) newErrors.b1 = t.global.err_b1;
    if (!form.b2.trim()) newErrors.b2 = t.global.err_b2;
    if (!form.b3.trim()) newErrors.b3 = t.global.err_b3;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        document
          .querySelector(`[name="${firstErrorField}"]`)
          ?.closest(".question-container")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
      if (!surveyData[surveyId]) surveyData[surveyId] = { sections: {} };

      surveyData[surveyId].sections.b = {
        ...form,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem("surveyData", JSON.stringify(surveyData));

      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.b = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      const payload = {
        municipality_id: state?.municipality_id || 1,
        ward_id: state?.ward_id || 5,
        district_id: state?.district_id || 10,
        lattitude_longitude: state?.lattitude_longitude || "22.5726,88.3639",
        survey_canteen_type: state?.surveyType || "kiosk",
        program_name: state?.area_name || "demo kiosk",
        current_section: "assessment",
        kiosk_location_fulladdress: state?.fullAddress || "N/A",
        area_id: state?.area_id || state?.id,
        canteen_type_id: state?.canteen_type || 2,
        reporting_date: new Date().toISOString().split("T")[0],
        quali_quanti_asmnt_B1: ratingValueMap[form.b1],
        quali_quanti_asmnt_B2: ratingValueMap[form.b2],
        quali_quanti_asmnt_B3: yesNoValueMap[form.b3],
      };

      dispatch(createCanteen(payload))
        .then(() => {
          setIsSubmitting(false);
          navigate("/survey/sections", { state });
        })
        .catch((err) => {
          setIsSubmitting(false);
          console.error("Server submission error:", err);
          alert(t.global.alert_server_error);
        });
    } catch (err) {
      setIsSubmitting(false);
      console.error("Error saving section B locally:", err);
      alert(t.global.alert_local_error);
    }
  };

  const totalQuestions = 3;
  const answeredCount = [form.b1, form.b2, form.b3].filter(Boolean).length;
  const progressPercent = Math.round((answeredCount / totalQuestions) * 100);
  const isFormValid = form.b1 && form.b2 && form.b3;

  // Reusable error icon
  const ErrorIcon = () => (
    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-2xl font-bold mb-6">{t.modules.kiosk.sections.B.title}</h2>

          {/* Validation Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 text-red-500 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-red-700 font-medium">{t.global.err_complete_all}</p>
              </div>
            </div>
          )}

          {/* B1 */}
          <div className="question-container border rounded-lg p-4 mb-6">
            <label className="block font-medium mb-2">
              B1. {t.modules.kiosk.sections.B.questions.quality}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ratingOptions.map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex items-center p-3 border rounded cursor-pointer transition-all duration-200 ${
                    form.b1 === value
                      ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="b1"
                    value={value}
                    checked={form.b1 === value}
                    onChange={handleChange}
                    className="mr-3 h-5 w-5 text-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
            {errors.b1 && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ErrorIcon />
                {errors.b1}
              </p>
            )}
          </div>

          {/* B2 */}
          <div className="question-container border rounded-lg p-4 mb-6">
            <label className="block font-medium mb-2">
              B2. {t.modules.kiosk.sections.B.questions.quantity}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {ratingOptions.map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex items-center p-3 border rounded cursor-pointer transition-all duration-200 ${
                    form.b2 === value
                      ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="b2"
                    value={value}
                    checked={form.b2 === value}
                    onChange={handleChange}
                    className="mr-3 h-5 w-5 text-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
            {errors.b2 && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ErrorIcon />
                {errors.b2}
              </p>
            )}
          </div>

          {/* B3 */}
          <div className="question-container border rounded-lg p-4 mb-6">
            <label className="block font-medium mb-2">
              B3. {t.modules.kiosk.sections.B.questions.items}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {yesNoOptions.map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex items-center p-3 border rounded cursor-pointer transition-all duration-200 ${
                    form.b3 === value
                      ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                      : "hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="b3"
                    value={value}
                    checked={form.b3 === value}
                    onChange={handleChange}
                    className="mr-3 h-5 w-5 text-blue-600"
                  />
                  {label}
                </label>
              ))}
            </div>
            {errors.b3 && (
              <p className="mt-2 text-sm text-red-600 flex items-center">
                <ErrorIcon />
                {errors.b3}
              </p>
            )}
          </div>

          {/* Progress Indicator */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>{t.global.progress_label}</span>
              <span>
                {progressPercent}% {t.global.progress_complete}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center mt-8 pt-6 border-t">
            <button
              className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors duration-200"
              onClick={() => navigate("/survey/sections", { state })}
              disabled={isSubmitting}
            >
              {t.global.back_to_sections}
            </button>

            <button
              className={`px-6 py-3 rounded-lg transition-all duration-200 flex items-center ${
                isFormValid && !isSubmitting
                  ? "bg-green-600 hover:bg-green-700 text-white"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t.global.submitting}
                </>
              ) : (
                t.global.submit_section_b
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionBQualityQuantity;